import { createAsyncThunk } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import moment from 'moment';

import { ApiResListsType, OhsApiRequestName } from 'global-services/api/OhsApiModels';
import {
  AllocatedListType,
  OhsSafetyPlanFilterPayload,
  OhsSafetyPlanHistoryFilterPayload,
  OhsSafetyPlanRootState,
} from 'safety-plan/OhsSafetyPlanModels';
import OhsSafetyPlanRecord from 'safety-plan/OhsSafetyPlanRecord';
// eslint-disable-next-line import/no-cycle
import {
  getSafetyPlanAllocatedList,
  getSafetyPlanCopyList,
  getSafetyPlanHistoryList,
  getSafetyPlanLists,
  getSafetyPlanListsWithAllocations,
  getSafetyPlanScheduledLists,
} from 'safety-plan/OhsSafetyPlanServices';
import globalModuleSearch from 'search/OhsSearchServices';
import { getSafetyPlanSchedListViewPreset } from 'safety-plan/register/utils/OhsSafetyPlanUtils';
import { OhsSafetyPlanViewPresets } from 'dashboard/topFilter/OhsSafetyPlanFilterModels';
import OhsFetchAll from 'global-services/OhsFetchAll';
import OhsUserLocalServices from 'user/OhsUserLocalServices';
import { OhsUser } from 'user/OhsUser';
import { ModuleType } from 'global-services/constants/OhsObject';
import globalModuleSearchHistory from 'search/OhsSearchHistoryServices';

enum RPCTypes {
  List = 'list',
  Scheduled = 'scheduled',
  History = 'history',
}

export const safetyPlanSearch = async (
  searchDetails: any,
  apiRequestName?: OhsApiRequestName
): Promise<ApiResListsType<OhsSafetyPlanRecord[]> | null> => {
  const globalSearchRes: any = await globalModuleSearch(searchDetails, apiRequestName);
  return globalSearchRes.result;
};

export const safetyPlanSearchHistory = async (
  searchDetails: any
): Promise<ApiResListsType<OhsSafetyPlanRecord[]> | null> => {
  const globalSearchRes: any = await globalModuleSearchHistory(searchDetails);
  return globalSearchRes.result;
};

export const safetyPlanListRPC = async ({
  safetyPlanState,
  safetyPlanFilters,
  type,
  count,
  orgAdminTierId,
  safetyPlanId,
}: {
  safetyPlanState: OhsSafetyPlanRootState;
  safetyPlanFilters: OhsSafetyPlanFilterPayload;
  type: RPCTypes;
  count: boolean;
  orgAdminTierId?: string;
  safetyPlanId?: string;
}): Promise<ApiResListsType<any[]> | null> => {
  const { globalSearch } = safetyPlanState;
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const userTier = user?.tierNum ?? 0;
  const hasGlobalSearch = safetyPlanState.globalSearch.searchInfo.searchKey !== '';

  const getSearchFilter = () => {
    const { filter } = globalSearch.searchInfo;
    if (type === RPCTypes.Scheduled) {
      return {
        ...globalSearch.searchInfo,
        filter: {
          skipOrgRecords: filter.skipOrgRecords,
          workplaces: filter.workplaces,
          page: filter.page,
          count,
          archived: filter.archived,
        },
      };
    }

    if (type === RPCTypes.List) {
      return {
        ...globalSearch.searchInfo,
        filter: { ...filter, count, skipAllocatedRecords: true },
      };
    }

    if (type === RPCTypes.History) {
      const payload = {
        ...globalSearch.searchInfo,
        filter: {
          _id: safetyPlanId,
          type: ModuleType.SafetyPlan,
          page: filter.page,
          count,
          workplaces: filter.workplaces,
          skipOrgRecords: filter.skipOrgRecords,
        },
        sort: globalSearch.searchInfo.sort && JSON.parse(globalSearch.searchInfo.sort),
      };
      if (!globalSearch.searchInfo.sort) {
        delete payload.sort;
      }

      return payload;
    }

    return { ...globalSearch.searchInfo, filter: { ...filter, count } };
  };

  const withViewPermission = user?.configs.safetyplan?.PERMISSIONS.view;

  if (userTier) {
    if (userTier === 1 && orgAdminTierId) {
      // no global search
      return getSafetyPlanLists(safetyPlanFilters, orgAdminTierId);
    }

    if (withViewPermission) {
      if (type === RPCTypes.Scheduled) {
        if (userTier === 2 || userTier === 3 || userTier === 4) {
          return hasGlobalSearch
            ? safetyPlanSearch(getSearchFilter(), OhsApiRequestName.SafetyPlanScheduleSearch)
            : getSafetyPlanScheduledLists(safetyPlanFilters);
        }
      }
      if (type === RPCTypes.List) {
        if (userTier === 2 || userTier === 4) {
          return hasGlobalSearch
            ? safetyPlanSearch(getSearchFilter())
            : getSafetyPlanLists(safetyPlanFilters);
        }
        if (userTier === 3) {
          return getSafetyPlanListsWithAllocations(
            safetyPlanState,
            safetyPlanFilters,
            getSearchFilter()
          );
        }
      }
      if (type === RPCTypes.History) {
        return hasGlobalSearch
          ? safetyPlanSearchHistory(getSearchFilter())
          : getSafetyPlanHistoryList(safetyPlanFilters, safetyPlanId || '');
      }
    }
  }
  return null;
};

export const fetchSafetyPlanCopyListsAsync = createAsyncThunk<ApiResListsType<any[]>, undefined>(
  'safetyPlan/fetchSafetyPlanCopyList',
  async (_, thunkAPI): Promise<ApiResListsType<any[]> | any> => {
    try {
      const response = await OhsFetchAll(getSafetyPlanCopyList, {
        archived: false,
        sort: {
          order: 1,
          key: '_id',
        },
      });
      const resVal = response;
      return resVal;
    } catch (err: any) {
      const error: AxiosError<any> = err; // cast the error for access
      if (!error.response) {
        throw err;
      }

      return thunkAPI.rejectWithValue(err.response.data);
    }
  }
);

export const fetchSafetyPlanListsCountAsync = createAsyncThunk<
  ApiResListsType<OhsSafetyPlanRecord[]> | null,
  { orgAdminTierId: string | undefined; tabKey: string | null }
>('safetyPlan/fetchSafetyPlanCountList', async ({ orgAdminTierId }, thunkAPI) => {
  const {
    safetyPlan,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsSafetyPlanRootState;
  const { currentViewPreset } = safetyPlan;
  const viewPresetFilter = filterInfo.safetyPlanModule[currentViewPreset];
  const safetyPlanState = thunkAPI.getState() as OhsSafetyPlanRootState;
  const safetyPlanFilters: OhsSafetyPlanFilterPayload = {
    ...viewPresetFilter,
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filterInfo.safetyPlanModule[currentViewPreset].sort)) },
  };

  try {
    const response = safetyPlanListRPC({
      safetyPlanState,
      safetyPlanFilters,
      type: RPCTypes.List,
      count: true,
      orgAdminTierId,
    });
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchSafetyPlanListsAsync = createAsyncThunk<
  ApiResListsType<OhsSafetyPlanRecord[]> | null,
  { orgAdminTierId: string | undefined; tabKey: string | null }
>('safetyPlan/fetchSafetyPlanList', async ({ orgAdminTierId }, thunkAPI) => {
  const {
    safetyPlan,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsSafetyPlanRootState;

  const safetyPlanState = thunkAPI.getState() as OhsSafetyPlanRootState;
  const data = safetyPlan.safetyPlanLists;
  const { currentPage, currentViewPreset } = safetyPlan;
  const viewPresetFilter = filterInfo.safetyPlanModule[currentViewPreset];

  const safetyPlanFilters: OhsSafetyPlanFilterPayload = {
    ...viewPresetFilter,
    next: currentPage > 10 ? data?.pagination.next ?? '' : undefined,
    // remove page if it is greater than 10
    ...(currentPage <= 10 && { page: currentPage }),
    sort: { ...JSON.parse(String(filterInfo.safetyPlanModule[currentViewPreset].sort)) },
    count: false,
  };

  try {
    const response = safetyPlanListRPC({
      safetyPlanState,
      safetyPlanFilters,
      type: RPCTypes.List,
      count: false,
      orgAdminTierId,
    });
    return await response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchSafetyPlanAllocatedListsAsync = createAsyncThunk<
  AllocatedListType[],
  { _ids: string[] }
>('safetyPlan/fetchSafetyPlanAllocatedList', async (safetyPlanIDs, thunkAPI): Promise<any> => {
  try {
    const response = await getSafetyPlanAllocatedList(safetyPlanIDs._ids);
    const resVal = response;
    return resVal;
  } catch (err: any) {
    const error: AxiosError<any> = err; // cast the error for access
    if (!error.response) {
      throw err;
    }

    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchSafetyPlanScheduledListsCountAsync = createAsyncThunk<
  ApiResListsType<any> | null,
  string | null
>('safetyPlan/fetchSafetyPlanScheduledCountList', async (tabKey, thunkAPI) => {
  const {
    safetyPlan,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsSafetyPlanRootState;

  const safetyPlanState = thunkAPI.getState() as OhsSafetyPlanRootState;
  const { currentViewPreset } = safetyPlan;
  const viewPresetFilter = filterInfo.safetyPlanModule[currentViewPreset];

  const safetyPlanFilters: OhsSafetyPlanFilterPayload = {
    ...viewPresetFilter,
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filterInfo.safetyPlanModule[currentViewPreset].sort)) },
  };

  try {
    const response = await safetyPlanListRPC({
      safetyPlanState,
      safetyPlanFilters,
      type: RPCTypes.Scheduled,
      count: true,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

export const fetchSafetyPlanScheduledListsAsync = createAsyncThunk<
  ApiResListsType<any> | null,
  string | null
>('safetyPlan/fetchSafetyPlanScheduledList', async (tabKey, thunkAPI) => {
  const {
    safetyPlan,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsSafetyPlanRootState;
  const user = OhsUserLocalServices.getLocalOhsUser() as OhsUser;
  const safetyPlanState = thunkAPI.getState() as OhsSafetyPlanRootState;
  // load Register ViewPreset filters
  const userTierNum = user?.tierNum ?? 0;
  const viewPreset =
    getSafetyPlanSchedListViewPreset(userTierNum) ??
    OhsSafetyPlanViewPresets.View1SafetyPlanSchedList;

  const data = safetyPlan.safetyPlanLists;
  const { currentPage, currentViewPreset } = safetyPlan;
  const viewPresetFilter = filterInfo.safetyPlanModule[viewPreset];

  const safetyPlanFilters: OhsSafetyPlanFilterPayload = {
    ...viewPresetFilter,
    next: currentPage > 10 ? data?.pagination.next ?? '' : undefined,
    // remove page if it is greater than 10
    ...(currentPage <= 10 && { page: currentPage }),
    sort: { ...JSON.parse(String(filterInfo.safetyPlanModule[currentViewPreset].sort)) },
    count: false,
  };

  try {
    const response = await safetyPlanListRPC({
      safetyPlanState,
      safetyPlanFilters,
      type: RPCTypes.Scheduled,
      count: false,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});

// HISTORY
export const fetchSafetyPlanHistoryListAsync = createAsyncThunk<
  ApiResListsType<OhsSafetyPlanRecord[]> | null,
  string
>('safetyPlan/fetchSafetyPlanHistoryList', async (safetyPlanId, thunkAPI) => {
  const {
    safetyPlan,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsSafetyPlanRootState;
  const safetyPlanState = thunkAPI.getState() as OhsSafetyPlanRootState;

  const data = safetyPlan.safetyPlanLists;
  const { currentPage, currentViewPreset } = safetyPlan;
  const viewPresetFilter = filterInfo.safetyPlanModule[currentViewPreset];

  const safetyPlanFilters: OhsSafetyPlanHistoryFilterPayload = {
    ...viewPresetFilter,
    dateCompleted: filterInfo.safetyPlanModule.dateCompleted
      ? {
          start: moment(new Date(filterInfo.safetyPlanModule.dateCompleted.start)).format(
            'YYYY-MM-DD'
          ),
          end: moment(new Date(filterInfo.safetyPlanModule.dateCompleted.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    next: currentPage > 10 ? data?.pagination?.next ?? '' : '',
    page: currentPage > 10 ? 0 : currentPage,
    sort: { ...JSON.parse(String(filterInfo.safetyPlanModule[currentViewPreset].sort)) },
    count: false,
  };

  try {
    const response = await safetyPlanListRPC({
      safetyPlanState,
      safetyPlanFilters,
      type: RPCTypes.History,
      count: false,
      safetyPlanId,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
export const fetchSafetyPlanHistoryListCountAsync = createAsyncThunk<
  ApiResListsType<OhsSafetyPlanRecord[]> | null,
  string
>('safetyPlan/fetchSafetyPlanHistoryCountList', async (safetyPlanId, thunkAPI) => {
  const {
    safetyPlan,
    globalfilter: { filterInfo },
  } = thunkAPI.getState() as OhsSafetyPlanRootState;

  const safetyPlanState = thunkAPI.getState() as OhsSafetyPlanRootState;
  const { currentViewPreset } = safetyPlan;
  const viewPresetFilter = filterInfo.safetyPlanModule[currentViewPreset];

  const safetyPlanFilters: OhsSafetyPlanHistoryFilterPayload = {
    ...viewPresetFilter,
    dateCompleted: filterInfo.safetyPlanModule.dateCompleted
      ? {
          start: moment(new Date(filterInfo.safetyPlanModule.dateCompleted.start)).format(
            'YYYY-MM-DD'
          ),
          end: moment(new Date(filterInfo.safetyPlanModule.dateCompleted.end)).format('YYYY-MM-DD'),
        }
      : undefined,
    count: true,
    page: 1,
    sort: { ...JSON.parse(String(filterInfo.safetyPlanModule[currentViewPreset].sort)) },
  };

  try {
    const response = await safetyPlanListRPC({
      safetyPlanState,
      safetyPlanFilters,
      type: RPCTypes.History,
      count: true,
      safetyPlanId,
    });
    return response;
  } catch (err: any) {
    return thunkAPI.rejectWithValue(err.response.data);
  }
});
